import React, { useContext, useMemo } from 'react';

// DEPENDENCIES
import isUndefined from 'lodash/isUndefined';
import { format, toZonedTime } from 'date-fns-tz';
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { NEW_USED, RETAIL_WHOLESALE } from 'codes';
import { DATE_FORMATS, INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// GLOBAL FUNCTIONS
import { formatDollar } from 'functions.js';

// GLOBAL HELPERS/CONTEXT
import useRecordView from 'helpers/getRecordView';
import { RecordContext } from 'helpers/getRecordData';

// LOCAL COMPONENTS
import Card from 'parts/card/Card';
import Sprite from 'core/tools/Sprite';
import Attachment from 'routers/interface/tools/attachment/Attachment';
import Form from 'core/form/Form';
import DmsTooltip from '../tools/DmsTooltip';

// STYLES
import styles from './inventoryInternal.module.scss';

// MAIN COMPONENT
const PricingInfoForm = () => {
  // CONTEXT
  const { record: { inventory = {}, ...record } = {} } = useContext(RecordContext);
  const { isView, isAdd } = useRecordView();

  const { values: { sale_type_category_id } = {} } = useFormikContext() || {};

  // MEMOS
  const {
    date_dms_sync,
    dms_invoice_price,
    dmsinvoice,
    new_used,
    retail_price,
    internet_price,
    invoice_cost,
    invoice_cost_with_inventory_pack,
    invoice_cost_with_repair_invoice
  } = useMemo(() => (isAdd ? record : inventory), [record, inventory, isAdd]);

  const isWholesale = useMemo(
    () => parseInt(sale_type_category_id) === RETAIL_WHOLESALE.wholesale.numeric,
    [sale_type_category_id]
  );

  const isNew = useMemo(() => new_used === NEW_USED.new.numeric, [new_used]);
  const type = useMemo(
    () => (!isUndefined(new_used) ? (isNew ? 'Factory Invoice Cost' : 'ACV') : ''),
    [new_used, isNew]
  );

  // If now, convert to EST
  const easternTimeZone = 'America/New_York';
  const lastDmsSync = useMemo(() => {
    const date = date_dms_sync ? new Date(date_dms_sync) : toZonedTime(new Date(), easternTimeZone);
    const formatted = format(date, DATE_FORMATS.dateTime, { timeZone: easternTimeZone });
    return !date_dms_sync ? (
      <div className="d-flex align-items-center">
        <Sprite className="me-1 text-danger" use="cancel" /> {formatted}
      </div>
    ) : (
      formatted
    );
  }, [date_dms_sync]);

  // RENDER
  return (
    <Form.Body>
      <Form.PlainText label="Retail" value={retail_price} schema="dollars" />
      {isNew ? (
        /* On Add Sale page don't show MSRP Sticker if it's Wholesale  */
        !isWholesale && (
          <Attachment.Upload
            label="MSRP Sticker/Window Sticker"
            name="inventoryDocumentAttachment.msrp"
            documentType={INVENTORY_FILE_TYPE_IDS.MSRP_STICKER_NEW}
            documentCode="msrp"
            uploadType="inventory"
            readonly={isView}
            requestedFor="Inventory"
          />
        )
      ) : (
        <Attachment.Upload
          label="Purchase Invoice"
          name="inventoryDocumentAttachment.invoice"
          documentType={INVENTORY_FILE_TYPE_IDS.PURCHASE_INVOICE_USED}
          documentCode="invoice"
          uploadType="inventory"
          readonly={isView}
          requestedFor="Inventory"
        />
      )}
      <Form.PlainText label="Internet" value={internet_price} schema="dollars" />
      <Form.PlainText label={`${type}`} value={invoice_cost} schema="dollars" />
      {isNew && (
        <Attachment.Upload
          label="Factory Invoice"
          name="inventoryDocumentAttachment.invoice"
          documentType={INVENTORY_FILE_TYPE_IDS.FACTORY_INVOICE_NEW}
          documentCode="invoice"
          uploadType="inventory"
          readonly={isView}
          requestedFor="Inventory"
        />
      )}
      <Form.PlainText
        label={`${type} with Inventory Pack Total`}
        value={invoice_cost_with_inventory_pack}
        schema="dollars"
      />
      <Form.PlainText
        label={`${type} with Repair Invoices`}
        value={invoice_cost_with_repair_invoice}
        schema="dollars"
      />
      <Form.PlainText
        label="DMS Cost"
        suffix={<DmsTooltip />}
        className={styles.span}
        fullWidth
        value={`${dms_invoice_price ? formatDollar(dms_invoice_price) : 'Not available'} ${dmsinvoice}`}
      />
      <Form.PlainText
        label="Last DMS Sync"
        suffix={<DmsTooltip />}
        className={styles.span}
        fullWidth
        value={lastDmsSync}
      />
    </Form.Body>
  );
};

export default function PricingInfo(props) {
  return <Card title="Pricing Info" body={<PricingInfoForm {...props} />} />;
}
